import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, CheckCircle2, ChevronDown, ChevronRight, Clock, History, Link2, MapPin, Package, Paperclip, RotateCcw, SearchX, TriangleAlert, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { EmptyState } from '@/components/ui/empty-state';
import { formatDate, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  ChipSelect,
  ExportMenu,
  LinkButton,
  PersonAvatar,
  ScrollFade,
  SearchField,
  Segmented,
  TableFoot,
  TableScroll,
  TableWrap,
  useMediaQuery,
  numClass,
  tableClass,
  tdClass,
  thClass
} from './InventoryUi';
import {
  INVENTORY_LOCATIONS,
  MOVEMENT_FILTERS,
  MOVEMENT_KIND_LABEL,
  MOVEMENT_KIND_TONE,
  MOVEMENT_PERIODS,
  documentLabel,
  documentRoute,
  formatQty,
  groupMovements,
  isDirectToSite,
  itemDataIssue,
  locationShort,
  movementDirection,
  movementMatchesFilter,
  paginate,
  plural,
  signedQty,
  siteDnReceipt,
  stockInOut,
  withinPeriod
} from './inventoryModel';
import { downloadCsv, downloadXlsx } from './exportRows';
import MovementDocumentPanel from './MovementDocumentPanel';
import StockFlowCard from './StockFlowCard';

const signed = (value) => {
  const number = Number(value) || 0;
  if (number > 0) return `+${formatQty(number)}`;
  if (number < 0) return `−${formatQty(Math.abs(number))}`;
  return formatQty(number);
};

export default function MovementsTab({
  movements = [],
  search = '',
  onSearchChange,
  itemDescriptions = {},
  activeFilter = 'all',
  onFilterChange,
  projects = [],
  onRecordMovement,
  selectedKey = null,
  onSelectDocument,
  onFixItem,
  fixLabelOf = () => 'Fix in Catalog',
  onPeriodChange,
  capped = false,
  cappedLimit = 2000,
  panelProps = {}
}) {
  const [period, setPeriodState] = useState(null);
  // The page holds the newest lines only: a period reaching further back is loaded by the page (date_from).
  const setPeriod = (value) => { setPeriodState(value); onPeriodChange?.(value); };
  const rootRef = useRef(null);
  // Phones: one compact row per document (date · document · route | signed qty) instead of the 720px table.
  const isPhone = useMediaQuery('(max-width: 639px)');
  const [locationFilter, setLocationFilter] = useState(null);
  const [projectFilter, setProjectFilter] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const projectNames = useMemo(() => new Map(projects.map((project) => [project.id, project.name])), [projects]);
  const projectSystems = useMemo(() => new Map(projects.map((project) => [project.id, project.system_type])), [projects]);
  const descriptionOf = (movement) => movement.description || itemDescriptions[movement.part_number] || '';

  // Period + location + project + search narrow the movements; the type counts follow them.
  const narrowed = useMemo(() => {
    const query = search.trim().toLowerCase();
    return movements.filter((movement) => {
      if (period && !withinPeriod(movement.date || movement.created_at, period)) return false;
      if (locationFilter && movement.source_location !== locationFilter && movement.destination_location !== locationFilter) return false;
      if (projectFilter && movement.project_id !== projectFilter) return false;
      return true;
    }).filter((movement) => {
      if (!query) return true;
      const reference = String(movement.reference || '');
      const haystack = [movement.part_number, movement.description || itemDescriptions[movement.part_number], reference, movement.reason, movement.user]
        .filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(query) || documentLabel(groupMovements([movement])[0]).toLowerCase().includes(query);
    });
  }, [itemDescriptions, locationFilter, movements, period, projectFilter, search]);

  const allGroups = useMemo(() => groupMovements(narrowed), [narrowed]);
  // Stock flow (21 #7) follows the period and project; its bands and boxes set the type / location filters.
  const flowMovements = useMemo(() => movements.filter((movement) => (
    (!period || withinPeriod(movement.date || movement.created_at, period))
    && (!projectFilter || movement.project_id === projectFilter)
  )), [movements, period, projectFilter]);
  const applyFlowFilter = ({ type, location }) => {
    if (type !== undefined) onFilterChange?.(type && type !== activeFilter ? type : 'all');
    if (location !== undefined) setLocationFilter(location);
  };
  const typeCounts = useMemo(() => Object.fromEntries(MOVEMENT_FILTERS.map((filter) => [
    filter.key,
    allGroups.filter((group) => group.movements.some((movement) => movementMatchesFilter(movement, filter.key))).length
  ])), [allGroups]);
  const groups = useMemo(() => allGroups.filter((group) => group.movements.some((movement) => movementMatchesFilter(movement, activeFilter))), [activeFilter, allGroups]);

  useEffect(() => { setPage(1); }, [search, period, locationFilter, projectFilter, activeFilter, pageSize]);

  const paged = paginate(groups, page, pageSize);
  // The open document follows the data (a reversal or attachment reloads the ledger).
  const selectedGroup = selectedKey ? allGroups.find((group) => group.key === selectedKey) || null : null;
  const linesTotal = groups.reduce((sum, group) => sum + group.lines, 0);
  // Transfers and direct-to-site deliveries never change the stock total, so they count in neither.
  const { in: inTotal, out: outTotal } = stockInOut(groups.flatMap((group) => group.movements));

  // Every count on the toolbar is in documents (the type control counts documents too), not ledger lines.
  const allDocuments = useMemo(() => groupMovements(movements), [movements]);
  const locationOptions = INVENTORY_LOCATIONS.map((location) => ({
    value: location,
    label: location,
    count: allDocuments.filter((group) => group.movements.some((movement) => movement.source_location === location || movement.destination_location === location)).length
  }));
  const projectOptions = useMemo(() => {
    const counts = new Map();
    allDocuments.forEach((group) => {
      new Set(group.movements.map((movement) => movement.project_id).filter(Boolean)).forEach((projectId) => {
        counts.set(projectId, (counts.get(projectId) || 0) + 1);
      });
    });
    return [...counts.entries()]
      .map(([id, count]) => ({ value: id, label: projectNames.get(id) || 'Unknown project', count }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [allDocuments, projectNames]);
  const periodOptions = MOVEMENT_PERIODS.map((option) => ({ value: option.value, label: option.label }));
  const periodLabel = MOVEMENT_PERIODS.find((option) => option.value === period)?.label;

  useEffect(() => {
    if (!selectedKey) return undefined;
    if (typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 1280px)').matches) return undefined;
    const frame = typeof window.requestAnimationFrame === 'function' ? window.requestAnimationFrame : (callback) => setTimeout(callback, 0);
    frame(() => rootRef.current?.querySelector('[data-testid="movement-document-panel"]')?.scrollIntoView?.({ block: 'start', behavior: 'smooth' }));
    return undefined;
  }, [selectedKey]);

  const isExpanded = (group) => Boolean(search.trim()) || Boolean(expanded[group.key]);
  const toggle = (group) => setExpanded((current) => ({ ...current, [group.key]: !isExpanded(group) }));

  const exportLines = groups.flatMap((group) => group.movements.map((movement) => ({ group, movement })));
  const exportColumns = [
    { header: 'Date', value: ({ movement }) => formatDate(movement.date || movement.created_at) },
    { header: 'Type', value: ({ group }) => MOVEMENT_KIND_LABEL[group.kind] },
    { header: 'Document', value: ({ group }) => documentLabel(group) },
    { header: 'From', value: ({ group, movement }) => (group.kind === 'GRN' || isDirectToSite(movement) ? 'Supplier' : movement.source_location || '') },
    { header: 'To', value: ({ group, movement }) => (group.kind === 'site-dn' ? (projectNames.get(movement.project_id) || 'Site') : movement.destination_location || '') },
    { header: 'Project', value: ({ movement }) => projectNames.get(movement.project_id) || '' },
    { header: 'Part number', value: ({ movement }) => movement.part_number },
    { header: 'Description', value: ({ movement }) => descriptionOf(movement) },
    { header: 'Qty', numeric: true, value: ({ movement }) => signedQty(movement) },
    { header: 'By', value: ({ movement }) => movement.user || '' },
    { header: 'Reference', value: ({ movement }) => movement.reference || '' }
  ];
  const exportName = `stock-movements-${periodLabel ? periodLabel.toLowerCase() : 'all-time'}`;
  const hasFilters = Boolean(search || period || locationFilter || projectFilter || activeFilter !== 'all');
  const clearFilters = () => {
    onSearchChange?.('');
    setPeriod(null);
    setLocationFilter(null);
    setProjectFilter(null);
    onFilterChange?.('all');
  };

  return (
    <div ref={rootRef} className="space-y-4" data-testid="inventory-movements-tab">
      <StockFlowCard
        movements={flowMovements}
        periodLabel={periodLabel || 'All time'}
        onFilter={applyFlowFilter}
        activeLocation={locationFilter}
        activeType={activeFilter}
        canRecordSiteDn={Boolean(onRecordMovement)}
        onRecordSiteDn={() => onRecordMovement?.('issue')}
      />
      <div className="flex flex-wrap items-center gap-2 xl:flex-nowrap">
        <SearchField value={search} onChange={onSearchChange} placeholder="Part no., GRN / DN…" className="w-full shrink-0 sm:w-[220px]" label="Search movements" />
        <ChipSelect label="Period" icon={CalendarDays} value={period} options={periodOptions} onChange={setPeriod} allLabel="All time" testId="movements-chip-period" />
        <ChipSelect label="Location" icon={locationFilter ? MapPin : undefined} value={locationFilter} options={locationOptions} onChange={setLocationFilter} testId="movements-chip-location" />
        <ChipSelect label="Project" value={projectFilter} options={projectOptions} onChange={setProjectFilter} emptyText="No movement is linked to a project yet" testId="movements-chip-project" />
        <span className="hidden flex-1 xl:block" />
        <ScrollFade className="w-full sm:w-auto xl:shrink" testId="movements-type-scroll">
          <Segmented
            className="max-w-none"
            ariaLabel="Movement type"
            value={activeFilter}
            onChange={onFilterChange}
            options={MOVEMENT_FILTERS.map((filter) => ({
              key: filter.key,
              label: filter.label,
              count: typeCounts[filter.key],
              testId: `movement-filter-${filter.key}`
            }))}
          />
        </ScrollFade>
      </div>

      <div className={cn('grid items-start gap-4', selectedGroup && 'xl:grid-cols-[minmax(0,1fr)_360px]')}>
      <TableWrap data-testid="movements-ledger">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line-2 px-4 py-2.5">
          <p className="text-[13px] text-ink-3">
            <b className="font-heading text-[15px] font-bold text-ink">Ledger</b>
            <span className="ml-2">{plural(groups.length, 'document')} · {plural(linesTotal, 'line')}</span>
          </p>
          <ExportMenu
            testId="movements-export-btn"
            disabled={!exportLines.length}
            onExcel={() => downloadXlsx({ filename: exportName, sheetName: 'Movements', columns: exportColumns, rows: exportLines })}
            onCsv={() => downloadCsv({ filename: exportName, columns: exportColumns, rows: exportLines })}
          />
        </div>
        {isPhone ? (
          <ul className="divide-y divide-line-2" data-testid="movements-phone-list">
            {paged.rows.map((group) => {
              const selected = selectedKey === group.key;
              const projectName = projectNames.get(group.projectId) || group.movements[0]?.project_name;
              const route = group.kind === 'GRN' && !group.direct
                ? `${group.movements[0]?.supplier_name || 'Supplier'} → ${locationShort(group.destination)}`
                : documentRoute(group, projectName);
              const receipt = siteDnReceipt(group);
              const total = group.neutral ? group.qtyTotal : group.signedTotal;
              return (
                <li key={group.key}>
                  <button
                    type="button"
                    onClick={() => (onSelectDocument ? onSelectDocument(selected ? null : group.key) : toggle(group))}
                    aria-current={selected ? 'true' : undefined}
                    className={cn('flex min-h-[60px] w-full items-center gap-3 px-4 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring', selected ? 'bg-brand-soft shadow-[inset_3px_0_0_var(--brand)]' : 'active:bg-surface-hover')}
                    data-testid={`movement-doc-${group.key}`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <StatusPill tone={MOVEMENT_KIND_TONE[group.kind] || 'gray'} label={MOVEMENT_KIND_LABEL[group.kind]} size="sm" />
                        <b className={cn('truncate text-[13.5px] font-bold text-ink', group.reference && group.kind !== 'adjustment' && 'font-mono text-[13px]')}>{documentLabel(group)}</b>
                        {receipt ? <ReceiptBadge receipt={receipt} /> : null}
                      </span>
                      <span className="mt-0.5 block truncate text-[12px] text-ink-3">{formatDate(group.date, { year: 'auto' })} · {route}</span>
                    </span>
                    <span className="shrink-0 text-right">
                      <b className={cn('block text-[15px] font-bold tabular-nums', group.neutral ? 'text-ink-2' : total > 0 ? 'text-status-green-ink' : 'text-ink')}>{group.neutral ? formatQty(total) : signed(total)}</b>
                      <span className="block text-[11.5px] text-ink-3">{plural(group.lines, 'line')}</span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-ink-4" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
        <TableScroll>
          <table className={cn(tableClass, 'min-w-[720px] table-fixed')}>
            <colgroup>
              <col className="w-9" />
              <col className="w-[104px]" />
              <col className="w-[100px]" />
              <col />
              <col className="w-[58px]" />
              <col className="w-[68px]" />
              <col className={selectedGroup ? 'w-[150px]' : 'w-[190px]'} />
            </colgroup>
            <thead>
              <tr>
                <th className={thClass}><span className="sr-only">Expand</span></th>
                <th className={cn(thClass, 'text-ink')}>Date ↓</th>
                <th className={thClass}>Type</th>
                <th className={thClass}>Document</th>
                <th className={cn(thClass, numClass)}>Items</th>
                <th className={cn(thClass, numClass)}>Qty</th>
                <th className={thClass}>By</th>
              </tr>
            </thead>
            <tbody>
              {paged.rows.map((group) => {
                const open = isExpanded(group);
                const selected = selectedKey === group.key;
                const projectName = projectNames.get(group.projectId) || group.movements[0]?.project_name;
                const system = projectSystems.get(group.projectId) || group.movements[0]?.project_system;
                const first = group.movements[0] || {};
                const supplier = first.supplier_name;
                const route = group.kind === 'GRN' && !group.direct
                  ? `${supplier || 'Supplier'} → ${selectedGroup ? locationShort(group.destination) : (group.destination || '—')}`
                  : documentRoute(group, projectName);
                const reversed = group.movements.some((movement) => movement.reversed_by_movement_id);
                const receipt = siteDnReceipt(group);
                const isReversal = group.movements.some((movement) => movement.reverses_movement_id);
                const tone = MOVEMENT_KIND_TONE[group.kind] || 'gray';
                const total = group.neutral ? group.qtyTotal : group.signedTotal;
                return (
                  <Fragment key={group.key}>
                    <tr
                      className={cn('cursor-pointer', selected || open ? '[&>td]:bg-brand-soft' : '[&:hover>td]:bg-surface-hover', selected && '[&>td:first-child]:shadow-[inset_3px_0_0_var(--brand)]')}
                      onClick={() => (onSelectDocument ? onSelectDocument(selected ? null : group.key) : toggle(group))}
                      aria-expanded={open}
                      aria-current={selected ? 'true' : undefined}
                      data-testid={`movement-doc-${group.key}`}
                    >
                      <td className={cn(tdClass, 'pl-3 pr-0')}>
                        <button
                          type="button"
                          className="grid h-6 w-6 place-items-center rounded text-ink-3 hover:bg-surface-hover hover:text-ink"
                          aria-label={`${open ? 'Hide' : 'Show'} lines of ${documentLabel(group)}`}
                          onClick={(event) => { event.stopPropagation(); toggle(group); }}
                        >
                          {open ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
                        </button>
                      </td>
                      <td className={cn(tdClass, 'whitespace-nowrap text-ink-2')}>{formatDate(group.date)}</td>
                      <td className={tdClass}>
                        <StatusPill tone={tone} label={MOVEMENT_KIND_LABEL[group.kind]} />
                      </td>
                      <td className={cn(tdClass, 'py-1.5')}>
                        <span className="flex min-w-0 items-center gap-1.5">
                          <span className={cn('truncate text-[13px] font-bold text-ink', group.reference && group.kind !== 'adjustment' && 'font-mono text-[12.5px]')}>
                            {documentLabel(group)}
                          </span>
                          {reversed ? <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-status-amber-bg px-1.5 text-[10.5px] font-bold text-status-amber-ink"><RotateCcw className="h-2.5 w-2.5" aria-hidden="true" />Reversed</span> : null}
                          {(first.attachments || []).length ? <Paperclip className="h-3 w-3 shrink-0 text-ink-3" aria-label="Has attachments" /> : null}
                          {receipt ? <ReceiptBadge receipt={receipt} /> : null}
                        </span>
                        <span className="mt-px flex min-w-0 items-center gap-1.5 text-[11.5px] text-ink-3">
                          <span className="truncate">
                            {[
                              route,
                              group.kind === 'adjustment' ? (first.reason || group.reference) : '',
                              ...(isReversal || group.kind === 'GRN' ? [] : group.notes.filter((note) => note !== first.reason && note !== group.reference))
                            ].filter(Boolean).join(' · ')}
                          </span>
                          {group.kind === 'site-dn' && system ? <span className="shrink-0 rounded border border-line bg-surface px-1 text-[10px] font-bold text-ink-2">{system}</span> : null}
                          {group.kind === 'GRN' && !group.direct ? (
                            <span className={cn('inline-flex shrink-0 items-center gap-0.5', first.lpo_number ? 'font-semibold text-ink-2' : 'text-ink-3')}>
                              <Link2 className="h-3 w-3" aria-hidden="true" />{first.lpo_number || 'No PO linked'}
                            </span>
                          ) : null}
                        </span>
                      </td>
                      <td className={cn(tdClass, numClass, 'text-ink-2')}>{formatNumber(group.lines)}</td>
                      <td
                        className={cn(tdClass, numClass, 'font-bold', group.neutral ? 'text-ink-2' : total > 0 ? 'text-status-green-ink' : total < 0 ? 'text-ink' : 'text-ink-2')}
                        title={group.direct ? 'Delivered straight from the supplier — stock did not change' : undefined}
                      >
                        {group.neutral ? formatQty(total) : signed(total)}
                      </td>
                      <td className={tdClass}>
                        <span className="flex min-w-0 items-center gap-2">
                          {group.user ? <PersonAvatar name={group.user} size={22} /> : null}
                          <span className="truncate text-ink-2">{group.user || '—'}</span>
                        </span>
                      </td>
                    </tr>
                    {open ? group.movements.map((movement, index) => {
                      const issue = itemDataIssue({ ...movement, description: descriptionOf(movement) });
                      const description = descriptionOf(movement);
                      const lineNeutral = movementDirection(movement) === 0;
                      return (
                        <tr key={movement.id || `${group.key}-${index}`} className="[&>td]:bg-surface-2">
                          <td className={cn(tdClass, 'h-9')} />
                          <td className={cn(tdClass, 'h-9')} colSpan={2} />
                          <td className={cn(tdClass, 'h-9')}>
                            <span className="flex min-w-0 items-center gap-2">
                              <span className="shrink-0 font-mono text-[12px] text-ink">{movement.part_number || '—'}</span>
                              {issue && issue !== 'No description' ? (
                                <>
                                  <span className="flex min-w-0 items-center gap-1 truncate text-[11.5px] text-status-amber-ink"><TriangleAlert className="h-3 w-3 shrink-0" aria-hidden="true" />{issue}</span>
                                  {onFixItem ? (
                                    <LinkButton className="shrink-0 text-[12px]" onClick={(event) => { event.stopPropagation(); onFixItem(movement.part_number); }} data-testid={`movement-fix-${movement.id || index}`}>
                                      {fixLabelOf(movement.part_number)}
                                    </LinkButton>
                                  ) : null}
                                </>
                              ) : (
                                <span className={cn('truncate text-[12px]', description ? 'text-ink-3' : 'text-status-amber-ink')}>{description || 'No description'}</span>
                              )}
                            </span>
                          </td>
                          <td className={cn(tdClass, 'h-9')} />
                          <td className={cn(tdClass, numClass, 'h-9 font-semibold', !lineNeutral && signedQty(movement) > 0 ? 'text-status-green-ink' : 'text-ink-2')}>
                            {lineNeutral ? formatQty(movement.qty) : signed(signedQty(movement))}
                          </td>
                          <td className={cn(tdClass, 'h-9 text-[12px] text-ink-3')}>
                            {movement.source_location && group.kind !== 'GRN' && group.kind !== 'site-dn' ? movement.source_location : ''}
                          </td>
                        </tr>
                      );
                    }) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
        )}
        {!paged.rows.length ? (
          hasFilters ? (
            <EmptyState
              icon={SearchX}
              title="No movements found"
              text={activeFilter !== 'all' && !typeCounts[activeFilter]
                ? `No ${MOVEMENT_FILTERS.find((filter) => filter.key === activeFilter)?.label || ''} documents have been recorded${periodLabel ? ` in the ${periodLabel.toLowerCase()}` : ''}.`
                : 'Nothing matches these filters.'}
              action={<Button size="sm" variant="outline" className="text-ink" onClick={clearFilters}>Clear filters</Button>}
            />
          ) : (
            <EmptyState
              icon={History}
              title="No stock movements yet"
              text="Every receipt, transfer, site delivery and adjustment is recorded here."
              action={onRecordMovement ? <Button size="sm" variant="outline" className="text-ink" onClick={() => onRecordMovement('GRN')}><Package aria-hidden="true" /> Record a GRN</Button> : null}
            />
          )
        ) : null}
        <TableFoot
          left={(
            <span>
              <b className="font-semibold text-ink-2">{plural(groups.length, 'document')}</b> · {plural(linesTotal, 'line')} ·{' '}
              <span className="text-status-green-ink">{formatQty(inTotal)} pcs in</span> · {formatQty(outTotal)} out
            </span>
          )}
          page={paged.page}
          pageCount={paged.pageCount}
          onPage={setPage}
          pageSize={pageSize}
          onPageSize={setPageSize}
        >
          {capped && !period ? (
            <span className="text-status-amber-ink" data-testid="movements-capped">Showing the newest {formatNumber(cappedLimit)} lines — pick a period to see older ones</span>
          ) : (
            <span>{periodLabel ? `Showing ${periodLabel.toLowerCase()}` : 'Showing all time'} · one row per document</span>
          )}
        </TableFoot>
      </TableWrap>
      {selectedGroup ? (
        <MovementDocumentPanel
          group={selectedGroup}
          projectName={projectNames.get(selectedGroup.projectId) || selectedGroup.movements[0]?.project_name || ''}
          projectSystem={projectSystems.get(selectedGroup.projectId) || selectedGroup.movements[0]?.project_system || ''}
          itemDescriptions={itemDescriptions}
          onClose={() => onSelectDocument?.(null)}
          onFixItem={onFixItem}
          fixLabelOf={fixLabelOf}
          {...panelProps}
        />
      ) : null}
      </div>
    </div>
  );
}

/** Site DN tracking in the ledger (17 #8 / 21 #9): Received · On the way · Not confirmed (after 48 h). */
function ReceiptBadge({ receipt }) {
  const meta = {
    received: { icon: CheckCircle2, text: 'Received', className: 'bg-status-green-bg text-status-green-ink', title: `Received by ${receipt.receivedBy || 'the site'}${receipt.receivedAt ? ` · ${formatDate(receipt.receivedAt)}` : ''}` },
    overdue: { icon: Clock, text: 'Not confirmed', className: 'bg-status-amber-bg text-status-amber-ink', title: 'Dispatched more than 48 hours ago and not confirmed on site' },
    dispatched: { icon: Truck, text: 'On the way', className: 'bg-[color:var(--status-gray-bg)] text-ink-2', title: 'Dispatched — waiting for the site to confirm' }
  }[receipt.state];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-0.5 rounded-full px-1.5 text-[10.5px] font-bold', meta.className)} title={meta.title} data-testid={`movement-receipt-badge-${receipt.state}`}>
      <Icon className="h-2.5 w-2.5" aria-hidden="true" />{meta.text}
    </span>
  );
}
